import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { FileText } from 'lucide-react';
import DownloadButton from '@/components/ui/download-button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface Doc {
  id: string;
  title: string;
  url?: string;
  category: string;
  date: string;
  file_size?: string;
  description?: string;
}

// Stała kolejność i czytelne etykiety kategorii.
// Klucze to znormalizowane wartości pola `category` z bazy.
const CATEGORY_ORDER: { key: string; label: string }[] = [
  { key: 'sprawozdania', label: 'Sprawozdania' },
  { key: 'regulaminy', label: 'Regulaminy' },
  { key: 'granty-dl', label: 'Granty DL' },
  { key: 'formularze', label: 'Formularze' },
  { key: 'inne', label: 'Inne' },
];

const normalizeCategory = (c: string) =>
  (c || 'inne').toLowerCase().trim().replace(/\s+/g, '-');

const formatLabel = (key: string) => {
  const known = CATEGORY_ORDER.find((c) => c.key === key);
  if (known) return known.label;
  // fallback: zamień myślniki na spacje i kapitalizuj pierwszą literę
  const pretty = key.replace(/-/g, ' ');
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
};

const DocumentsSection = () => {
  const { data: docs = [] } = useQuery<Doc[]>({
    queryKey: ['documents'],
    queryFn: async () => {
      const { data } = await supabase
        .from('documents')
        .select('*')
        .order('date', { ascending: false });
      return (data as Doc[]) || [];
    },
  });

  // Grupowanie dokumentów po znormalizowanej kategorii
  const grouped = docs.reduce<Record<string, Doc[]>>((acc, d) => {
    const key = normalizeCategory(d.category);
    (acc[key] ||= []).push(d);
    return acc;
  }, {});

  // Najpierw kategorie w stałej kolejności, potem ewentualne nieznane na końcu
  const orderedKeys = [
    ...CATEGORY_ORDER.map((c) => c.key).filter((k) => grouped[k]?.length),
    ...Object.keys(grouped)
      .filter((k) => !CATEGORY_ORDER.some((c) => c.key === k))
      .sort(),
  ];

  return (
    <section id="dokumenty" className="py-16 lg:py-24 bg-muted/50">
      <div className="container">
        <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-6">
          Dokumenty i pliki
        </h2>

        {docs.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">
            Dokumenty pojawią się wkrótce.
          </p>
        ) : (
          <Accordion type="multiple" className="w-full space-y-3">
            {orderedKeys.map((key) => {
              const items = grouped[key];
              return (
                <AccordionItem
                  key={key}
                  value={key}
                  className="bg-card border rounded-lg overflow-hidden border-b"
                >
                  <AccordionTrigger className="px-4 py-4 hover:no-underline">
                    <div className="flex flex-1 items-center gap-3 text-left">
                      <span className="font-semibold">{formatLabel(key)}</span>
                      <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">
                        {items.length}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-3">
                      {items.map((doc) => (
                        <div
                          key={doc.id}
                          className="bg-background rounded-lg p-4 flex items-center gap-4 border hover:border-secondary transition-colors"
                        >
                          <FileText className="w-8 h-8 text-secondary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold truncate">{doc.title}</p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                              <time>
                                {new Date(doc.date).toLocaleDateString('pl-PL')}
                              </time>
                              {doc.file_size && <span>{doc.file_size}</span>}
                            </div>
                          </div>
                          <DownloadButton
                            href={doc.url}
                            label="Pobierz"
                            fileTitle={doc.title}
                            className="flex-shrink-0 bg-transparent hover:bg-secondary/10 text-secondary px-2 py-1 focus-visible:ring-secondary"
                          />
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
    </section>
  );
};

export default DocumentsSection;
